import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core/common/event';
import { I18nService } from '@analytics/shared/services/i18n.interface';
import { LangTypeMode, PrimaryLang } from '../../enums';
import { DEFAULT_LANGUAGE } from '../../constants';
import { AITranslationOptionsService } from '../ai-translation-options-service';
import { OriginalOptionsService } from '../original-options-service';
import { TranslationOptionModel } from '../../models/translation-options-model';
import type { TranslationOptions, TargetPatent, Language } from '../../types';
import type { TranslationOptionsService } from '../translation-options-service';

@injectable()
export class TranslationOptionsServiceImpl implements TranslationOptionsService {
    private _onStatesChangeEmitter = new Emitter<TranslationOptions>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(I18nService)
        private _i18n: I18nService,
        @inject(TranslationOptionModel)
        private _model: TranslationOptionModel,
        @inject(AITranslationOptionsService)
        private _aiTranslationOptionsService: AITranslationOptionsService,
        @inject(OriginalOptionsService)
        private _originalOptionsService: OriginalOptionsService,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData() {
        return this._model.modelData;
    }

    private _getDefaultOption() {
        return [
            {
                key: DEFAULT_LANGUAGE,
                label: this._i18n.t('patent-view["Not translated2"]'),
                lang: DEFAULT_LANGUAGE,
                langType: LangTypeMode.DEFAULT,
            },
            {
                label: this._i18n.t('patent-view["Full translation"]'),
                lang: '',
                langType: LangTypeMode.DEFAULT,
            },
            {
                key: PrimaryLang.EN,
                label: 'English',
                lang: PrimaryLang.EN,
                langType: LangTypeMode.TRANSLATION,
            },
            {
                key: PrimaryLang.CN,
                label: '简体中文',
                lang: PrimaryLang.CN,
                langType: LangTypeMode.TRANSLATION,
            },
        ];
    }

    private _getJPTranslation() {
        return [
            {
                key: PrimaryLang.JP,
                label: '日本語',
                lang: PrimaryLang.JP,
                langType: LangTypeMode.TRANSLATION,
            },
        ];
    }

    private _getAITranslation(patentLang: string) {
        const aiOptions = this._aiTranslationOptionsService.initialize(patentLang);

        return aiOptions.map(item => ({
            key: item.lang,
            label: item.label,
            lang: item.lang,
            langType: LangTypeMode.TRANSLATION,
            tip: this._i18n.t('AIpatentTips'),
        }));
    }

    private _getOriginalOptions(patentLangList: string[]) {
        const originalOptions = this._originalOptionsService.initialize(patentLangList);

        return originalOptions.length ?
            [
                {
                    label: this._i18n.t('patent-view["Original data only"]'),
                    lang: '',
                    langType: LangTypeMode.DEFAULT,
                },
                ...originalOptions
                    .map(item => ({
                        key: item.lang,
                        label: item.label,
                        lang: item.lang,
                        langType: LangTypeMode.ORIGINAL,
                    })),
            ]
            : [];
    }

    public getOptionLabel(lang: Language) {
        const _lang = lang.toUpperCase();

        if (_lang === DEFAULT_LANGUAGE) return this._i18n.t('patent-view["Not translated"]');

        const matched = this.getModelData().find(item => item.lang === _lang);
        return matched?.label || '';
    }

    public initialize(target: TargetPatent) {
        const defaultOptions = this._getDefaultOption();
        const jpOptions = this._getJPTranslation();
        const aiOptions = this._getAITranslation(target.patentLang);
        const originOptions = this._getOriginalOptions(target.patentOriginLang);

        const options = target.isCompareView ?
            defaultOptions
            : [
                ...defaultOptions,
                ...jpOptions,
                ...aiOptions,
                ...originOptions,
            ];

        this._model.update(() => options);

        return options;
    }
}
